import { API_TYPES, AutomatedNameUpdateStatus } from "@clipboard-health/contract-worker-app-bff";
import { patch } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const updateAutomatedNameUpdateAttributesSchema = z.object({
  status: z.enum([AutomatedNameUpdateStatus.ACCEPTED, AutomatedNameUpdateStatus.FLAGGED]),
});

type UpdateAutomatedNameUpdateAttributes = z.infer<
  typeof updateAutomatedNameUpdateAttributesSchema
>;

const updateAutomatedNameUpdateRequestSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.automatedNameUpdate),
    attributes: updateAutomatedNameUpdateAttributesSchema,
  }),
});

const updateAutomatedNameUpdateResponseSchema = z.object({});

export type UpdateAutomatedNameUpdateRequest = z.infer<
  typeof updateAutomatedNameUpdateRequestSchema
>;
export type UpdateAutomatedNameUpdateResponse = z.infer<
  typeof updateAutomatedNameUpdateResponseSchema
>;

interface UpdateAutomatedNameUpdateParams {
  automatedNameUpdateId: string;
  attributes: UpdateAutomatedNameUpdateAttributes;
}

export function useUpdateAutomatedNameUpdate(
  options: UseMutationOptions<
    UpdateAutomatedNameUpdateResponse,
    AxiosError,
    UpdateAutomatedNameUpdateParams
  > = {}
): UseMutationResult<
  UpdateAutomatedNameUpdateResponse,
  AxiosError,
  UpdateAutomatedNameUpdateParams
> {
  return useMutation({
    mutationFn: async ({ automatedNameUpdateId, attributes }: UpdateAutomatedNameUpdateParams) => {
      const response = await patch({
        url: `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/automated-name-updates/${automatedNameUpdateId}`,
        data: {
          data: {
            type: API_TYPES.automatedNameUpdate,
            attributes,
          },
        },
        requestSchema: updateAutomatedNameUpdateRequestSchema,
        responseSchema: updateAutomatedNameUpdateResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_AUTOMATED_NAME_UPDATE_FAILURE,
    },
    ...options,
  });
}
